import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Chip, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Boxes } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { configTemplateKind, conversantConfig } from './templates/config-template-kind';
import { CdnConfigTable } from './templates/cdn-config-table';
import { VCloudConfigTable } from './templates/vcloud-config-table';
import { ConversantConfigTable } from './templates/conversant-config-table';
import { VpsConfigTable } from './templates/vps-config-table';
import { HostingConfigTable } from './templates/hosting-config-table';
import { ColocationRackGrid, ColocationRackGridEmpty } from './templates/colocation-rack-grid';
import { findColocationUConfig } from './templates/colocation-rack-helpers';
import { BareMetalConfigTable } from './templates/baremetal-config-table';
import { EmailConfigTable } from './templates/email-config-table';
import { SslInfoCard } from './templates/ssl-info-card';

/** A flattened config row: either a main config option or an addon's option. */
interface ConfigRow extends ServiceConfigOption {
  _addonName?: string;
}

const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;
const num = (v?: number | null): ReactNode => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v ?? '—'}</span>;

/**
 * The service's provisioned configuration as the PRIMARY Information view (like
 * legacy): every config option (VM package / bandwidth commitment / storage …)
 * plus each addon service's options, with commitment + pricing columns. Read-only;
 * editing lives in the Configuration tab.
 */
export function ServiceConfigTable({ detail }: { detail: ServiceDetail }) {
  const kind = configTemplateKind(detail.service_template);
  if (kind === 'cdn') return <CdnConfigTable detail={detail} />;
  if (kind === 'vcloud') return <VCloudConfigTable detail={detail} />;
  if (kind === 'conversant') {
    const cfg = conversantConfig(detail.service_template);
    if (cfg) return <ConversantConfigTable detail={detail} config={cfg} />;
  }
  if (kind === 'vps') return <VpsConfigTable detail={detail} />;
  if (kind === 'hosting') return <HostingConfigTable detail={detail} />;
  if (kind === 'colocation') {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <GenericConfigTable detail={detail} />
        <ColocationRackSection detail={detail} />
      </div>
    );
  }
  if (kind === 'baremetal') return <BareMetalConfigTable detail={detail} />;
  if (kind === 'email') return <EmailConfigTable detail={detail} />;
  if (kind === 'ssl') return <SslInfoCard detail={detail} />;
  return <GenericConfigTable detail={detail} />;
}

/**
 * Rack-U assignment for Server.Colocation: rendered below the config table.
 * The grid only has content when a `colocation-u`/`u` configoption carries
 * `metadata.racks[]`; otherwise show the explicit empty state.
 */
function ColocationRackSection({ detail }: { detail: ServiceDetail }) {
  const base = useServiceConfigoptions(detail.service_id);
  const addons = useServiceAddonConfigoptions(detail.service_id);

  if (base.isLoading || base.isError) return null;

  const configoptions = base.data?.results ?? [];
  const addonRows = (addons.data?.results ?? []).map((a) => ({
    service_id: a.service_id,
    service_name: a.service_name,
    configoptions: a.configoptions ?? [],
  }));

  const hasRackConfig =
    !!findColocationUConfig(configoptions) ||
    addonRows.some((a) => !!findColocationUConfig(a.configoptions));
  if (!hasRackConfig) return <ColocationRackGridEmpty />;

  return <ColocationRackGrid configoptions={configoptions} addons={addonRows} />;
}

function GenericConfigTable({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, currency)}</span>;

  const rows: ConfigRow[] = [
    ...(base.data?.results ?? []),
    ...(addons.data?.results ?? []).flatMap((a) =>
      (a.configoptions ?? []).map((c) => ({ ...c, _addonName: a.service_name })),
    ),
  ];

  const columns: ColumnDef<ConfigRow, unknown>[] = [
    {
      id: 'name',
      header: 'Item',
      cell: ({ row }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>
          {row.original._addonName ? <Badge tone="info" appearance="subtle" size="sm">addon: {row.original._addonName}</Badge> : null}
        </span>
      ),
    },
    { id: 'type', header: 'Type', cell: ({ row }) => row.original.type || '—' },
    { id: 'unit', header: 'Unit', cell: ({ row }) => row.original.unit || '—' },
    { id: 'commitment_value', header: rightHeader('Commitment'), cell: ({ row }) => num(row.original.commitment_value) },
    { id: 'commitment_price_per_unit', header: rightHeader('Unit price'), cell: ({ row }) => money(row.original.commitment_price_per_unit) },
    { id: 'commitment_price_total', header: rightHeader('Total'), cell: ({ row }) => money(row.original.commitment_price_total) },
    {
      id: 'commitment_prepaid',
      header: 'Billing',
      cell: ({ row }) => (
        <Chip appearance="subtle" tone={row.original.commitment_prepaid ? 'success' : 'neutral'} size="sm">
          {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
        </Chip>
      ),
    },
    { id: 'available', header: rightHeader('Available'), cell: ({ row }) => num(row.original.commitment_available) },
  ];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => `${r.service_id}-${r.service_configoption_id}`}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="Pay as you go"
              description="No committed resources — this service is billed on usage."
            />
          }
        />
      </CardBody>
    </Card>
  );
}
